import React from 'react'
import { c, msgid, ngettext, t } from 'ttag'
import { ProgressType } from '../../../../services/api.types'
import { CustomTypeCategory, customTypes, customUnits, getCustomTypeTextById } from '../../../../services/CustomTypes'
import { getPeriodString } from '../../../../common/helpers/date'
import { formatThousands } from '../../../../common/helpers/translations'
import { consistencyUnit, dailyGoalUnit, FormData } from '../../ChallengeAdmin'
import './ChallengeReview.scss'
import YumField from '../../../../components/YumInput/YumInput'
import EditIcon from '@material-ui/icons/Edit'
import InputAdornment from '@material-ui/core/InputAdornment'
import { IconButton, Step, StepContent, StepLabel, Stepper } from '@material-ui/core'
import { gray, primaryBlue } from '../../../../styles/material-ui-theme-v2'
import { PointsFormula } from '../../../ChallengeInfo/ChallengeInfo'

export interface IChallengeReviewProps {
  editMode: boolean
  data: FormData
  formStep: number
  setFormStep: any
}

export default function ChallengeReview(props: IChallengeReviewProps) {
  const { editMode, data, formStep, setFormStep } = props
  const {
    maxFormStep,

    // Step 0.
    companyId,
    companyName,
    newCommunityName,

    // Step 1.
    activityType,
    customActivityName,

    // Step 2.
    individualLeaderboard,
    groupLeaderboard,
    countryLeaderboard,
    //asCollectiveUnit,
    chatEnabled,
    manualEntryEnabled,
    roundDailyPoints,

    challengePoints,

    // Step 3.
    dateRange,
    days,

    // Step 4.
    goalType,
    totalGoal,
    collectiveGoal,
    persistenceGoal,
    dailyGoal,
    customUnit,
    customType,
    indexTarget,
    simpleManualEntry,

    // Step 5.
    title,
    prize,
    description,
  } = data

  const setStep = (step: number) => () => {
    setFormStep(step)
  }

  const Field = ({ step, ...props }: any) => (
    <YumField
      variant={props.label ? 'filled' : 'outlined'}
      disabled
      InputProps={{
        endAdornment: step !== false && (
          <InputAdornment position={'end'}>
            <IconButton edge="end" onClick={setStep(step)}>
              <EditIcon style={{ color: primaryBlue }} />
            </IconButton>
          </InputAdornment>
        ),
      }}
      style={{
        marginBottom: 0,
      }}
      {...props}
    />
  )

  const isPopularCustomActivity = customTypes.some((ct) => ct.id === activityType)
  const customTypeUnit = customUnits.find((cu) => cu.id === customType)?.text

  const company = companyId === 'new' ? newCommunityName /*+ ' (' + t`new community` + ')'*/ : companyName
  const stepZero = companyId && <Field step={editMode ? false : 0} /*label={t`Organizer`}*/ value={company} />

  const stepOne = activityType && (
    <Field
      step={1}
      /*label={t`Activity type`}*/
      value={activityType === ProgressType.CUSTOM ? customActivityName : getCustomTypeTextById(activityType)}
    />
  )

  const configuration = []
  //if (asCollectiveUnit) configuration.push(t`Collective goal`)
  if (individualLeaderboard) configuration.push(t`Individual leaderboard`)
  if (groupLeaderboard) configuration.push(t`Group leaderboard`)
  if (countryLeaderboard) configuration.push(t`Country leaderboard`)
  if (chatEnabled) configuration.push(t`Chat`)
  if (manualEntryEnabled) configuration.push(t`Manual entry`)
  if (roundDailyPoints) configuration.push(t`Rounding of daily points`)
  const stepTwo = configuration.length && (
    <>
      {activityType === ProgressType.POINTS && (
        <span
          style={{
            marginBottom: '10px',
            padding: '10px',
            borderRadius: '10px',
            backgroundColor: gray,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <PointsFormula pointsLogic={challengePoints} isReview={true} style={{ fontSize: '13px' }} />
          <InputAdornment position={'end'} style={{ marginRight: 5 }}>
            <IconButton edge="end" onClick={setStep(2)}>
              <EditIcon style={{ color: primaryBlue }} />
            </IconButton>
          </InputAdornment>
        </span>
      )}
      <Field step={2} /*label={t`Configuration`}*/ value={configuration.join('\n')} multiline />
    </>
  )

  const periodString =
    dateRange && dateRange[0] && dateRange[1]
      ? getPeriodString(dateRange[0].getTime(), dateRange[1].getTime(), true)
      : ''
  const daysText = days ? ngettext(msgid`day`, `days`, days) : ''
  const period = `${periodString} (${days} ${daysText})`
  const stepThree = dateRange && <Field step={3} /*label={t`Period`}*/ value={period} />

  const stepFourCollective = collectiveGoal && (
    <Field
      step={4}
      label={t`Collective goal`}
      value={`${formatThousands(collectiveGoal)} ${
        activityType === ProgressType.CUSTOM
          ? customUnit
          : isPopularCustomActivity
          ? customTypeUnit
          : dailyGoalUnit(activityType as ProgressType, customUnit)
      }`}
    />
  )

  const stepFourOther = (
    <>
      {((goalType === 'totalAmount' && activityType !== ProgressType.CUSTOM && !isPopularCustomActivity) ||
        goalType === 'consistentDays') &&
      dailyGoal ? (
        <Field
          step={4}
          label={t`Daily goal`}
          value={
            `${formatThousands(dailyGoal)} ${dailyGoalUnit(activityType as ProgressType, customType, customUnit)}` +
            (simpleManualEntry ? ` (${t`simple entry`})` : '')
          }
          style={{
            marginBottom: '10px',
          }}
        />
      ) : (
        ''
      )}
      {!['consistentDays', 'consistentDaysBinary'].includes(goalType) && totalGoal && totalGoal > 0 ? (
        <Field
          step={4}
          label={t`Total goal`}
          value={`${formatThousands(totalGoal)} ${
            activityType === ProgressType.CUSTOM ? customUnit : dailyGoalUnit(activityType, customType)
          }`}
        />
      ) : (
        ''
      )}
      {['consistentDays', 'consistentDaysBinary'].includes(goalType) && persistenceGoal ? (
        <Field
          step={4}
          label={t`Consistency goal`}
          value={`${formatThousands(persistenceGoal)} ${consistencyUnit(days)}`}
        />
      ) : (
        ''
      )}
    </>
  )

  const stepFourActivityIndex = (
    <>
      {indexTarget && (
        <Field
          step={4}
          label={t`Target`}
          value={`${indexTarget}%`}
          style={{
            marginBottom: goalType === 'consistentDays' && persistenceGoal ? '10px' : 0,
          }}
        />
      )}
      {goalType === 'consistentDays' && persistenceGoal && (
        <Field step={4} label={t`Consistency goal`} value={`${persistenceGoal} ${consistencyUnit(days)}`} />
      )}
    </>
  )

  const stepFive = (
    <>
      {title && (
        <Field
          step={5}
          label={t`Challenge name`}
          value={title}
          style={{
            marginBottom: prize || description ? '10px' : 0,
          }}
        />
      )}
      {prize && (
        <Field
          step={5}
          label={t`Prize`}
          value={prize}
          style={{
            marginBottom: description ? '10px' : 0,
          }}
        />
      )}
      {description && <Field step={5} label={t`Description`} value={description} multiline />}
    </>
  )

  const steps = [
    { label: t`Organizer`, content: stepZero },
    { label: t`Activity type`, content: stepOne },
    { label: t`Configuration`, content: stepTwo },
    { label: t`Period`, content: stepThree },
    {
      label: t`Goal`,
      content: (
        <>
          {/*asCollectiveUnit*/}
          {goalType === 'collective' ? (
            stepFourCollective
          ) : (
            <>
              {activityType === ProgressType.ALL ? stepFourActivityIndex : ''}
              {activityType !== ProgressType.ALL ? stepFourOther : ''}
            </>
          )}
        </>
      ),
    },
    { label: t`Details`, content: stepFive },
  ]

  return (
    <div className="challenge-review">
      <Stepper activeStep={formStep} orientation="vertical">
        {steps.map(({ label, content }, index) => (
          <Step key={`step-${index}`} expanded={index < maxFormStep} disabled={false}>
            <StepLabel>{label}</StepLabel>
            {index < maxFormStep ? <StepContent>{content}</StepContent> : ''}
          </Step>
        ))}
      </Stepper>
    </div>
  )
}
